/**
 * <cart-drawer>
 * Slide-in cart drawer for the CLAUDIO theme.
 * Self-contained: handles open/close, fetch, render, qty updates and removals.
 */
class CartDrawer extends HTMLElement {
  // Tiered rewards (cents). Order matters — lowest to highest threshold.
  static REWARD_TIERS = [
    { cents: 5000,  label: 'Shaker Gratis',    icon: '🥤' },
    { cents: 10000, label: 'Accesorio Gratis', icon: '🎒' },
    { cents: 20000, label: 'Producto Gratis',  icon: '🎁' },
  ];
  static OPEN_ATTR = 'open';

  constructor() {
    super();
    this._cart = null;
    this._isOpen = false;
    this._isUpdating = false;
    this._lastFocused = null;
    this._onKeydown = this._onKeydown.bind(this);
    this._onCartAdded = this._onCartAdded.bind(this);
    this._onOpenTrigger = this._onOpenTrigger.bind(this);
  }

  connectedCallback() {
    // Build internal structure if the user provided just <cart-drawer></cart-drawer>
    if (!this.querySelector('[data-cart-drawer-body]')) {
      this._buildShell();
    }
    this._cacheRefs();
    this._bindStaticEvents();

    // Global listeners
    document.addEventListener('click', this._onOpenTrigger);
    document.addEventListener('cart:added', this._onCartAdded);
    document.addEventListener('keydown', this._onKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onOpenTrigger);
    document.removeEventListener('cart:added', this._onCartAdded);
    document.removeEventListener('keydown', this._onKeydown);
  }

  // ---------- DOM building ----------
  _buildShell() {
    this.innerHTML = `
      <div class="cart-drawer__backdrop" data-cart-backdrop aria-hidden="true"></div>
      <aside class="cart-drawer__panel" role="dialog" aria-modal="true" aria-label="Carrito de compras" tabindex="-1">
        <header class="cart-drawer__header">
          <h2 class="cart-drawer__title">Tu carrito <span class="cart-drawer__count" data-cart-header-count></span></h2>
          <button type="button" class="cart-drawer__close" data-cart-close aria-label="Cerrar carrito">
            <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none"/></svg>
          </button>
        </header>
        <div class="cart-drawer__shipping" data-cart-shipping></div>
        <div class="cart-drawer__body" data-cart-drawer-body aria-live="polite"></div>
        <footer class="cart-drawer__footer" data-cart-footer hidden>
          <div class="cart-drawer__subtotal">
            <span class="cart-drawer__subtotal-label">Subtotal</span>
            <strong class="cart-drawer__subtotal-amount" data-cart-subtotal>$0.00</strong>
          </div>
          <form action="/cart" method="post" class="cart-drawer__checkout-form" novalidate>
            <button type="submit" name="checkout" class="cart-drawer__checkout" data-cart-checkout>
              <span>Proceder al pago</span>
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
          </form>
          <a href="/cart" class="cart-drawer__view-cart">Ver carrito completo</a>
        </footer>
      </aside>
    `;
  }

  _cacheRefs() {
    this.$backdrop = this.querySelector('[data-cart-backdrop]');
    this.$panel = this.querySelector('.cart-drawer__panel');
    this.$close = this.querySelector('[data-cart-close]');
    this.$body = this.querySelector('[data-cart-drawer-body]');
    this.$footer = this.querySelector('[data-cart-footer]');
    this.$subtotal = this.querySelector('[data-cart-subtotal]');
    this.$shipping = this.querySelector('[data-cart-shipping]');
  }

  _bindStaticEvents() {
    if (this.$backdrop) this.$backdrop.addEventListener('click', () => this.close());
    if (this.$close) this.$close.addEventListener('click', () => this.close());
    if (this.$body) {
      this.$body.addEventListener('click', (e) => this._onBodyClick(e));
      this.$body.addEventListener('change', (e) => this._onBodyChange(e));
    }
  }

  // ---------- External triggers ----------
  _onOpenTrigger(e) {
    const trigger = e.target.closest('[data-action="open-cart"]');
    if (!trigger) return;
    e.preventDefault();
    this.open();
  }

  _onCartAdded() {
    // product-form fires this — open the drawer and refresh state.
    this.open();
  }

  _onKeydown(e) {
    if (!this._isOpen) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      this.close();
      return;
    }
    if (e.key === 'Tab') {
      this._trapFocus(e);
    }
  }

  // ---------- Open / close ----------
  async open() {
    if (this._isOpen) {
      await this._refresh();
      return;
    }
    this._lastFocused = document.activeElement;
    this._isOpen = true;
    this.setAttribute(CartDrawer.OPEN_ATTR, '');
    this.classList.add('is-open');
    // Decorative slide-in. Removed after to avoid stale animation state.
    this.classList.add('cart-drawer--animate');
    setTimeout(() => this.classList.remove('cart-drawer--animate'), 350);
    if (this.$backdrop) this.$backdrop.style.opacity = '1';
    if (this.$backdrop) this.$backdrop.style.pointerEvents = 'auto';
    const overlay = this.querySelector('.cart-drawer__overlay');
    if (overlay) {
      overlay.style.opacity = '1';
      overlay.style.pointerEvents = 'auto';
    }
    document.documentElement.classList.add('cart-drawer-open');
    document.body.classList.add('cart-drawer-open');
    if (this.$panel) {
      try { this.$panel.focus({ preventScroll: true }); } catch (_) {}
    }
    await this._refresh();
  }

  close() {
    if (!this._isOpen) return;
    this._isOpen = false;
    this.removeAttribute(CartDrawer.OPEN_ATTR);
    this.classList.remove('is-open');
    if (this.$backdrop) {
      this.$backdrop.style.opacity = '';
      this.$backdrop.style.pointerEvents = '';
    }
    const overlay = this.querySelector('.cart-drawer__overlay');
    if (overlay) {
      overlay.style.opacity = '';
      overlay.style.pointerEvents = '';
    }
    document.documentElement.classList.remove('cart-drawer-open');
    document.body.classList.remove('cart-drawer-open');
    if (this._lastFocused && typeof this._lastFocused.focus === 'function') {
      try { this._lastFocused.focus(); } catch (_) {}
    }
  }

  // ---------- Networking ----------
  async _refresh() {
    if (!this.$body) return;
    this.$body.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (!res.ok) throw new Error('Cart fetch failed');
      const cart = await res.json();
      this._cart = cart;
      this._render();
      this._updateBubble(cart.item_count);
    } catch (err) {
      this._renderError();
    } finally {
      this.$body.removeAttribute('aria-busy');
    }
  }

  async _changeLine(line, quantity) {
    if (this._isUpdating) return;
    this._isUpdating = true;
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ line, quantity })
      });
      if (!res.ok) throw new Error('Cart change failed');
      await this._refresh();
    } catch (err) {
      this._renderError();
    } finally {
      this._isUpdating = false;
    }
  }

  // ---------- Render ----------
  _render() {
    if (!this._cart || !this.$body) return;
    const cart = this._cart;

    if (!cart.items || cart.items.length === 0) {
      this._renderEmpty();
      return;
    }

    const itemsHtml = cart.items.map((item, idx) => this._renderLine(item, idx + 1)).join('');
    this.$body.innerHTML = `<ul class="cart-drawer__lines" role="list">${itemsHtml}</ul>`;

    if (this.$footer) this.$footer.hidden = false;
    if (this.$subtotal) this.$subtotal.textContent = this._formatMoney(cart.total_price);
    // Update header count
    const headerCount = this.querySelector('[data-cart-header-count]');
    if (headerCount) headerCount.textContent = `(${cart.item_count})`;
    this._renderShipping(cart.total_price);
  }

  _renderLine(item, line) {
    const url = this._escape(item.url || '#');
    const title = this._escape(item.product_title || item.title || '');
    const vendor = item.vendor ? `<p class="cart-line__vendor">${this._escape(item.vendor)}</p>` : '';
    const variant = item.variant_title && item.variant_title !== 'Default Title'
      ? `<p class="cart-line__variant">${this._escape(item.variant_title)}</p>`
      : '';
    const image = item.image
      ? `<img src="${this._escape(item.image)}" alt="${title}" loading="lazy" width="84" height="84">`
      : '';
    const price = this._formatMoney(item.final_line_price ?? item.line_price ?? item.price);
    const unitPrice = item.quantity > 1
      ? `<span class="cart-line__unit">${this._formatMoney(item.final_price ?? item.price)} c/u</span>`
      : '';

    return `
      <li class="cart-line" data-line="${line}" data-key="${this._escape(item.key || '')}">
        <a href="${url}" class="cart-line__media">${image}</a>
        <div class="cart-line__info">
          <div class="cart-line__top">
            <div class="cart-line__heading">
              ${vendor}
              <a href="${url}" class="cart-line__title">${title}</a>
              ${variant}
            </div>
            <button type="button" class="cart-line__remove" data-line-remove aria-label="Eliminar producto">
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
          </div>
          <div class="cart-line__bottom">
            <div class="cart-line__qty" role="group" aria-label="Cantidad">
              <button type="button" data-qty-minus aria-label="Disminuir">&minus;</button>
              <input type="number" value="${Number(item.quantity) || 0}" min="0" data-qty-input aria-label="Cantidad">
              <button type="button" data-qty-plus aria-label="Aumentar">+</button>
            </div>
            <div class="cart-line__pricing">
              <p class="cart-line__price">${price}</p>
              ${unitPrice}
            </div>
          </div>
        </div>
      </li>
    `;
  }

  _renderEmpty() {
    if (this.$body) {
      this.$body.innerHTML = `
        <div class="cart-drawer__empty">
          <p>Tu carrito está vacío</p>
          <button type="button" class="cart-drawer__continue" data-cart-continue>Seguir comprando</button>
        </div>
      `;
      const cont = this.$body.querySelector('[data-cart-continue]');
      if (cont) cont.addEventListener('click', () => this.close());
    }
    if (this.$footer) this.$footer.hidden = true;
    if (this.$shipping) this.$shipping.innerHTML = '';
  }

  _renderError() {
    if (!this.$body) return;
    this.$body.innerHTML = `<div class="cart-drawer__error" role="alert">No se pudo actualizar el carrito. Intenta de nuevo.</div>`;
  }

  _renderShipping(totalCents) {
    if (!this.$shipping) return;
    const tiers = CartDrawer.REWARD_TIERS;
    if (!tiers.length) { this.$shipping.innerHTML = ''; return; }

    // Determine the next unlocked tier (smallest tier with cents > totalCents)
    const nextTier = tiers.find(t => totalCents < t.cents);
    const allUnlocked = !nextTier;
    const maxCents = tiers[tiers.length - 1].cents;
    const pct = Math.max(0, Math.min(100, (totalCents / maxCents) * 100));

    // Build the headline message
    let headline;
    if (allUnlocked) {
      headline = `<span class="cart-rewards__headline cart-rewards__headline--success">🎉 ¡Has desbloqueado todas las recompensas!</span>`;
    } else {
      const remaining = nextTier.cents - totalCents;
      headline = `Te faltan <strong>${this._formatMoney(remaining)}</strong> para <strong>${nextTier.label}</strong>`;
    }

    // Build milestone segments
    const segments = tiers.map((tier, i) => {
      const reached = totalCents >= tier.cents;
      const isNext = !reached && tier === nextTier;
      return `
        <div class="cart-rewards__milestone${reached ? ' is-reached' : ''}${isNext ? ' is-next' : ''}">
          <div class="cart-rewards__dot" aria-hidden="true">
            ${reached ? '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><path d="M5 12l5 5L20 7" stroke="currentColor" stroke-width="3" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>' : `<span class="cart-rewards__dot-num">${i + 1}</span>`}
          </div>
          <div class="cart-rewards__milestone-info">
            <span class="cart-rewards__milestone-amount">$${(tier.cents / 100).toFixed(0)}</span>
            <span class="cart-rewards__milestone-label">${tier.label}</span>
          </div>
        </div>
      `;
    }).join('');

    this.$shipping.innerHTML = `
      <div class="cart-rewards${allUnlocked ? ' cart-rewards--all-unlocked' : ''}">
        <p class="cart-rewards__text">${headline}</p>
        <div class="cart-rewards__track" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${pct.toFixed(0)}" aria-label="Progreso de recompensas">
          <div class="cart-rewards__fill" style="width:${pct.toFixed(1)}%"></div>
        </div>
        <div class="cart-rewards__milestones">
          ${segments}
        </div>
      </div>
    `;
  }

  // ---------- Body events ----------
  _onBodyClick(e) {
    const remove = e.target.closest('[data-line-remove]');
    if (remove) {
      e.preventDefault();
      const line = this._getLineFor(remove);
      if (line) this._changeLine(line, 0);
      return;
    }
    const minus = e.target.closest('[data-qty-minus]');
    if (minus) {
      e.preventDefault();
      this._stepQty(minus, -1);
      return;
    }
    const plus = e.target.closest('[data-qty-plus]');
    if (plus) {
      e.preventDefault();
      this._stepQty(plus, 1);
      return;
    }
  }

  _onBodyChange(e) {
    const input = e.target.closest('[data-qty-input]');
    if (!input) return;
    const line = this._getLineFor(input);
    const qty = Math.max(0, parseInt(input.value, 10) || 0);
    if (line) this._changeLine(line, qty);
  }

  _stepQty(btn, delta) {
    const li = btn.closest('[data-line]');
    if (!li) return;
    const input = li.querySelector('[data-qty-input]');
    if (!input) return;
    const current = parseInt(input.value, 10) || 0;
    const next = Math.max(0, current + delta);
    input.value = String(next);
    const line = parseInt(li.dataset.line, 10);
    if (line) this._changeLine(line, next);
  }

  _getLineFor(node) {
    const li = node.closest('[data-line]');
    if (!li) return null;
    return parseInt(li.dataset.line, 10) || null;
  }

  // ---------- Helpers ----------
  _updateBubble(count) {
    const bubbles = document.querySelectorAll('[data-cart-count]');
    bubbles.forEach((b) => {
      b.textContent = String(count);
      b.toggleAttribute('hidden', count <= 0);
    });
  }

  _formatMoney(cents) {
    const n = (Number(cents) || 0) / 100;
    const formatted = n.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return `$${formatted}`;
  }

  _escape(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  _trapFocus(e) {
    if (!this.$panel) return;
    const focusables = this.$panel.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

// ============================================================
// Quick-add: handle clicks on .product-card__quick-add[data-variant-id]
// from anywhere on the site (collections, carousels, home, search).
// ============================================================
(function () {
  const QUICK_ADD_SELECTOR = '.product-card__quick-add[data-variant-id]';
  const DEFAULT_LABEL = 'Añadir';
  const LOADING_LABEL = '...';
  const ADDED_LABEL = '✓';

  document.addEventListener('click', function (e) {
    const btn = e.target.closest(QUICK_ADD_SELECTOR);
    if (!btn) return;
    if (btn.disabled || btn.getAttribute('aria-busy') === 'true') return;
    e.preventDefault();

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    const originalLabel = btn.textContent.trim() || DEFAULT_LABEL;
    btn.setAttribute('aria-busy', 'true');
    btn.textContent = LOADING_LABEL;

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData
    })
      .then(function (res) { return res.json().then(function (data) { return { ok: res.ok, data: data }; }); })
      .then(function (result) {
        if (!result.ok) {
          const msg = (result.data && (result.data.description || result.data.message)) || 'No se pudo añadir.';
          btn.textContent = msg.length > 14 ? 'Error' : msg;
          setTimeout(function () { btn.textContent = originalLabel; btn.removeAttribute('aria-busy'); }, 2200);
          return;
        }
        btn.textContent = ADDED_LABEL;
        document.dispatchEvent(new CustomEvent('cart:added', { bubbles: true, detail: result.data }));
        setTimeout(function () { btn.textContent = originalLabel; btn.removeAttribute('aria-busy'); }, 1400);
      })
      .catch(function () {
        btn.textContent = 'Error';
        setTimeout(function () { btn.textContent = originalLabel; btn.removeAttribute('aria-busy'); }, 2200);
      });
  });
})();
